import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import { useDispatch } from 'react-redux';
import { loginSuccess } from './store/slices/authSlice';
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

// Static Components
import Navbar from './components/layout/Navbar';
import Loading from './components/common/Loading';
import PrivacyPolicy from './components/pages/privacypolicy';
import TermsAndConditions from './components/pages/Terms';
import AccountDeletion from './components/pages/AccountDeletion';

// Lazy Loaded Components (For Performance Optimization)
const Home = lazy(() => import('./components/pages/Home'));
const Login = lazy(() => import('./components/auth/Login'));
const Register = lazy(() => import('./components/auth/Register'));
const SuperAdminDashboard = lazy(() => import('./components/superadmin/SuperAdminDashboard'));
const CanteenAdminDashboard = lazy(() => import('./components/canteen/CanteenAdminDashboard'));
const StaffDashboard = lazy(() => import('./components/staff/StaffDashboard'));
const CanteenMenu = lazy(() => import('./components/canteen/CanteenMenu'));
const ProtectedRoute = lazy(() => import('./components/routing/ProtectedRoute'));
const Checkout = lazy(() => import('./components/checkout/Checkout'));
const Profile = lazy(() => import('./components/profile/Profile'));
const ForgotPassword = lazy(() => import('./components/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/auth/ResetPassword'));
const AppDisplay = lazy(() => import('./components/pages/appdisplay'));

function App() {
    const dispatch = useDispatch();

    useEffect(() => {
        // Check if user data exists in localStorage
        const user = localStorage.getItem('user');
        if (user) {
            try {
                const userData = JSON.parse(user);
                dispatch(loginSuccess(userData));
            } catch (err) {
                console.error('Error parsing user data:', err);
                localStorage.removeItem('user');
            }
        }
    }, [dispatch]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
                <Navbar />
                <Suspense fallback={<Loading />}>
                    <Routes>
                        {/* Public Routes */}
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/reset-password/:resetToken" element={<ResetPassword />} />
                        <Route path="/app" element={<AppDisplay />} />
                        <Route path="/privacypolicy" element={<PrivacyPolicy />} />
                        <Route path="/termsandconditions" element={<TermsAndConditions />} />
                        <Route path="/account-deletion" element={<AccountDeletion />} />

                        {/* Protected Routes */}
                        <Route
                            path="/"
                            element={
                                <ProtectedRoute allowedRoles={['student']}>
                                    <Home />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/canteens/:id/menu"
                            element={
                                <ProtectedRoute allowedRoles={['student']}>
                                    <CanteenMenu />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/superadmin"
                            element={
                                <ProtectedRoute allowedRoles={['superAdmin']}>
                                    <SuperAdminDashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/canteen-admin"
                            element={
                                <ProtectedRoute allowedRoles={['canteenAdmin']}>
                                    <CanteenAdminDashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/staff"
                            element={
                                <ProtectedRoute allowedRoles={['staff']}>
                                    <StaffDashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/checkout"
                            element={
                                <ProtectedRoute allowedRoles={['student']}>
                                    <Checkout />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/profile"
                            element={
                                <ProtectedRoute>
                                    <Profile />
                                </ProtectedRoute>
                            }
                        />
                    </Routes>
                </Suspense>
            </Router>
            <Analytics />
            <SpeedInsights />
        </ThemeProvider>
    );
}

export default App;
